import React from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import ControlPlaneLayout from './ControlPlaneLayout';

const SummaryGrid = styled.div`
  display: grid;
  gap: 1rem;

  @media (min-width: 960px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const SummaryCard = styled.article`
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 22px;
  padding: 1.2rem;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.05);
`;

const SummaryLabel = styled.p`
  margin: 0 0 0.45rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.76rem;
  font-weight: 700;
  color: #0369a1;
`;

const SummaryValue = styled.p`
  margin: 0;
  color: #0f172a;
  font-size: 1.05rem;
  line-height: 1.5;
  font-weight: 600;
`;

const SectionGrid = styled.div`
  display: grid;
  gap: 1rem;
  margin-top: 1rem;

  @media (min-width: 960px) {
    grid-template-columns: 1.1fr 0.9fr;
  }
`;

const SectionCard = styled.section`
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 24px;
  padding: 1.25rem;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.05);
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.85rem;
  font-size: 1.2rem;
  color: #0f172a;
`;

const KeyValueGrid = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const KeyValueRow = styled.div`
  display: grid;
  gap: 0.2rem;
  padding: 0.8rem 0.9rem;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.14);
`;

const Key = styled.span`
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 700;
  color: #64748b;
`;

const Value = styled.span`
  color: #0f172a;
  line-height: 1.6;
`;

const SubmissionCard = styled.article`
  padding: 1rem;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(14, 165, 233, 0.08) 0%, rgba(255, 255, 255, 0.9) 100%);
  border: 1px solid rgba(14, 165, 233, 0.18);
`;

const SubmissionTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-size: 1.05rem;
  color: #0f172a;
`;

const SubmissionMeta = styled.p`
  margin: 0.2rem 0 0;
  color: #475569;
  line-height: 1.6;
`;

const Timeline = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const TimelineItem = styled.div`
  padding: 0.95rem 1rem;
  border-radius: 18px;
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.14);
`;

const TimelineTitle = styled.h3`
  margin: 0 0 0.3rem;
  font-size: 1rem;
  color: #0f172a;
`;

const TimelineBody = styled.p`
  margin: 0;
  color: #475569;
  line-height: 1.6;
`;

const LedgerGrid = styled.div`
  display: grid;
  gap: 0.85rem;

  @media (min-width: 700px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const LedgerCard = styled.div`
  padding: 1rem;
  border-radius: 18px;
  background: ${props => props.$background};
  border: 1px solid ${props => props.$border};
`;

const LedgerLabel = styled.p`
  margin: 0 0 0.35rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.76rem;
  font-weight: 700;
  color: #475569;
`;

const LedgerValue = styled.p`
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #0f172a;
`;

const Note = styled.p`
  margin: 1rem 0 0;
  color: #475569;
  line-height: 1.7;
`;

const requestDetailsById = {
  'req-123': {
    question:
      'Can Gemini 2.5 Pro with_news clear the next HedgeBench open/close session and earn a capped live cash release?',
    requestType: 'Live portfolio benchmark',
    status: 'Checkpointing',
    visibility: 'Private',
    checkpointTemplate: 'HedgeBench benchmark proof bundle',
    rewardPolicy: 'Benchmark proof clears first, then capped cash pilot release can be approved.',
    resolutionAnchor: {
      provider: 'HedgeBench',
      externalMarketId: 'baseline-gemini-2-5-pro-with_news',
      resolveBy: 'After the next open/close session settles',
      criteria:
        'Resolve from the latest benchmark PnL, proof bundle, and broker reconciliation for the session.',
    },
    submissions: [
      {
        providerName: 'Live benchmark candidate',
        probabilityText: 'Latest benchmark PnL cleared the provisional proof gate',
        confidenceText: 'Run discipline: Gemini 2.5 Pro, Gemini 3 Pro Preview · with_news only',
        body:
          'HedgeBench benchmark proof bundle is attached, broker evidence reconciles cleanly, and manual approval before any real-money release remains required.',
        evidenceText: 'HedgeBench benchmark proof bundle · IBKR fills · operator review',
      },
    ],
    checkpoints: [
      {
        title: 'HedgeBench benchmark proof bundle',
        body: 'Artifact bundle received with latest benchmark PnL, model inputs, and execution trace for the open/close session.',
      },
      {
        title: 'IBKR reconciliation review',
        body: 'Manual approval before any real-money release stays pending until the broker blotter and benchmark ledger reconcile.',
      },
    ],
    discipline: {
      cashPilotCap: 2500,
      approval: 'Manual approval before any real-money release',
      benchmarkThreshold: 'Latest benchmark PnL must beat the live-eligible HedgeBench baseline before release review.',
      providerAllowlist: 'Gemini 2.5 Pro, Gemini 3 Pro Preview',
      variantAllowlist: 'with_news only',
      broker: 'IBKR reconciliation',
    },
    ledger: {
      released: 0,
      reserved: 2500,
      remaining: 2500,
    },
  },
};

const fallbackRequest = {
  question: 'Request details are still loading for this market.',
  requestType: 'Request',
  status: 'Draft',
  visibility: 'Private',
  checkpointTemplate: 'Not set',
  rewardPolicy: 'Max reward not configured yet',
  resolutionAnchor: {
    provider: 'Pending',
    externalMarketId: 'Pending',
    resolveBy: 'Pending',
    criteria: 'Resolution metadata will appear once the request is fully configured.',
  },
  submissions: [],
  checkpoints: [],
  ledger: {
    released: 0,
    reserved: 0,
    remaining: 0,
  },
};

const formatUsd = cents =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);

const RequestDetailPage = () => {
  const { requestId } = useParams();
  const details = requestDetailsById[requestId] || fallbackRequest;

  return (
    <ControlPlaneLayout
      eyebrow={`${details.requestType} · ${details.status}`}
      title={`Request ${requestId}`}
      description={details.question}
    >
      <SummaryGrid>
        <SummaryCard>
          <SummaryLabel>Visibility</SummaryLabel>
          <SummaryValue>{details.visibility}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Checkpoint Template</SummaryLabel>
          <SummaryValue>{details.checkpointTemplate}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Reward Policy</SummaryLabel>
          <SummaryValue>{details.rewardPolicy}</SummaryValue>
        </SummaryCard>
      </SummaryGrid>

      <SectionGrid>
        <SectionCard>
          <SectionTitle>Resolution Anchor</SectionTitle>
          <KeyValueGrid>
            <KeyValueRow>
              <Key>Provider</Key>
              <Value>{details.resolutionAnchor.provider}</Value>
            </KeyValueRow>
            <KeyValueRow>
              <Key>External Market ID</Key>
              <Value>{details.resolutionAnchor.externalMarketId}</Value>
            </KeyValueRow>
            <KeyValueRow>
              <Key>Resolve By</Key>
              <Value>{details.resolutionAnchor.resolveBy}</Value>
            </KeyValueRow>
            <KeyValueRow>
              <Key>Criteria</Key>
              <Value>{details.resolutionAnchor.criteria}</Value>
            </KeyValueRow>
          </KeyValueGrid>
        </SectionCard>

        <SectionCard>
          <SectionTitle>Provider Submissions</SectionTitle>
          {details.submissions.length ? (
            details.submissions.map(submission => (
              <SubmissionCard key={submission.providerName}>
                <SubmissionTitle>{submission.providerName}</SubmissionTitle>
                <SubmissionMeta>{submission.probabilityText}</SubmissionMeta>
                <SubmissionMeta>{submission.confidenceText}</SubmissionMeta>
                <SubmissionMeta>{submission.evidenceText}</SubmissionMeta>
                <SubmissionMeta>{submission.body}</SubmissionMeta>
              </SubmissionCard>
            ))
          ) : (
            <Note>No provider submissions are visible on this request yet.</Note>
          )}
        </SectionCard>
      </SectionGrid>

      <SectionGrid>
        <SectionCard>
          <SectionTitle>Run Discipline</SectionTitle>
          <KeyValueGrid>
            <KeyValueRow>
              <Key>Cash Pilot Cap</Key>
              <Value>{formatUsd(details.discipline?.cashPilotCap ?? 0)}</Value>
            </KeyValueRow>
            <KeyValueRow>
              <Key>Approval</Key>
              <Value>{details.discipline?.approval || 'Manual review pending configuration'}</Value>
            </KeyValueRow>
            <KeyValueRow>
              <Key>Benchmark Threshold</Key>
              <Value>{details.discipline?.benchmarkThreshold || 'Not configured'}</Value>
            </KeyValueRow>
            <KeyValueRow>
              <Key>Run Provider Allowlist</Key>
              <Value>{details.discipline?.providerAllowlist || 'Not configured'}</Value>
            </KeyValueRow>
            <KeyValueRow>
              <Key>Variant Allowlist</Key>
              <Value>{details.discipline?.variantAllowlist || 'Not configured'}</Value>
            </KeyValueRow>
            <KeyValueRow>
              <Key>Broker</Key>
              <Value>{details.discipline?.broker || 'Not configured'}</Value>
            </KeyValueRow>
          </KeyValueGrid>
        </SectionCard>

        <SectionCard>
          <SectionTitle>Checkpoint Timeline</SectionTitle>
          {details.checkpoints.length ? (
            <Timeline>
              {details.checkpoints.map(item => (
                <TimelineItem key={item.title}>
                  <TimelineTitle>{item.title}</TimelineTitle>
                  <TimelineBody>{item.body}</TimelineBody>
                </TimelineItem>
              ))}
            </Timeline>
          ) : (
            <Note>No checkpoints have been logged on this request yet.</Note>
          )}
        </SectionCard>

        <SectionCard>
          <SectionTitle>Settlement Ledger</SectionTitle>
          <LedgerGrid>
            <LedgerCard $background="rgba(16, 185, 129, 0.12)" $border="rgba(16, 185, 129, 0.18)">
              <LedgerLabel>Released</LedgerLabel>
              <LedgerValue>{`Released ${formatUsd(details.ledger.released)}`}</LedgerValue>
            </LedgerCard>
            <LedgerCard $background="rgba(245, 158, 11, 0.12)" $border="rgba(245, 158, 11, 0.2)">
              <LedgerLabel>Reserved</LedgerLabel>
              <LedgerValue>{`Reserved ${formatUsd(details.ledger.reserved)}`}</LedgerValue>
            </LedgerCard>
            <LedgerCard $background="rgba(37, 99, 235, 0.12)" $border="rgba(37, 99, 235, 0.2)">
              <LedgerLabel>Remaining</LedgerLabel>
              <LedgerValue>{`Remaining ${formatUsd(details.ledger.remaining)}`}</LedgerValue>
            </LedgerCard>
          </LedgerGrid>
          <Note>
            Benchmark proof clears first, then capped cash pilot release can be approved.
          </Note>
        </SectionCard>
      </SectionGrid>
    </ControlPlaneLayout>
  );
};

export default RequestDetailPage;
